import axios from 'axios';

// Base URL from environment (supports different deployments)
// Production: Backend at testtelecall.b100x.in, Frontend at telecaller.b100x.in
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://testtelecall.b100x.in/api';
// For local development, uncomment below:
// const BASE_URL = 'http://localhost:5001/api'

// Backend origin without the /api prefix. Used to resolve publicly-served
// upload URLs (e.g. /uploads/attachments/...) which are mounted at the
// server root, not under /api.
export const API_ORIGIN = BASE_URL.replace(/\/api\/?$/, '');

// Resolve an attachment/upload path to a full URL. Already-absolute URLs
// (http/https) are returned untouched.
export const filePublicUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    // Check if this is a superadmin route
    const isSuperAdminRoute = config.url?.startsWith('/superadmin');

    // For superadmin routes, use superadmin_token
    // For other routes, use admin_auth_token
    let token;
    if (isSuperAdminRoute) {
      token = localStorage.getItem('superadmin_token');
    } else {
      token = localStorage.getItem('admin_auth_token');
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Increase timeout for uploads
    if (config.url?.includes('/upload')) {
      config.timeout = 5 * 60 * 1000; // 5 minutes
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Log all errors for debugging
    console.error('API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    });

    // Handle 401 Unauthorized - but don't immediately redirect
    if (error.response?.status === 401) {
      const isLoginPage = window.location.pathname === '/login';
      const isSuperAdminLoginPage = window.location.pathname === '/superadmin/login';

      if (!isLoginPage && !isSuperAdminLoginPage) {
        console.warn('Auth token expired or invalid');

        // Check if this was a superadmin request
        const isSuperAdminRoute = error.config?.url?.startsWith('/superadmin');

        if (isSuperAdminRoute) {
          // Clear superadmin storage
          localStorage.removeItem('superadmin_token');
          localStorage.removeItem('superadmin_user');
        } else {
          // Clear admin storage
          localStorage.removeItem('admin_auth_token');
          localStorage.removeItem('admin_user_data');
        }
        // Don't redirect - let the app handle it naturally
        // The AuthContext/SuperAdminContext will detect missing token and show login
      }
    }
    return Promise.reject(error);
  }
);

// ========================================
// AUTH API
// ========================================

export const authAPI = {
  login: (identifier, password) =>
    api.post('/auth/login', { email: identifier, password }),

  logout: () => api.post('/auth/logout'),

  getProfile: () => api.get('/auth/me'),

  updateProfile: (data) => api.put('/auth/profile', data),

  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/password', { currentPassword, newPassword }),
};

// ========================================
// USERS API
// ========================================

export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),

  getById: (id) => api.get(`/users/${id}`),

  create: (data) => api.post('/users', data),

  update: (id, data) => api.put(`/users/${id}`, data),

  delete: (id) => api.delete(`/users/${id}`),

  bulkUpdate: (data) => api.post('/users/bulk-update', data),

  getPerformance: (id, params) => api.get(`/users/${id}/performance`, { params }),
};

// ========================================
// LEADS API
// ========================================

export const leadsAPI = {
  getAll: (params) => api.get('/telecalling/leads', { params }),

  getMy: (params) => api.get('/telecalling/leads/my', { params }),

  getById: (id) => api.get(`/telecalling/leads/${id}`),

  create: (data) => api.post('/telecalling/leads', data),

  bulkCreate: (data) => api.post('/telecalling/leads/bulk', data),

  update: (id, data) => api.put(`/telecalling/leads/${id}`, data),

  delete: (id) => api.delete(`/telecalling/leads/${id}`),

  bulkDelete: (ids) => api.post('/telecalling/leads/bulk-delete', { ids }),

  bulkUpdate: (data) => api.post('/telecalling/leads/bulk-update', data),

  assign: (leadIds, userId) =>
    api.post('/telecalling/leads/assign', { leadIds, assignedTo: userId }),

  bulkAssign: (data) => api.post('/telecalling/leads/assign', data),

  // Assign leads by filters (count-based)
  assignByFilters: (data) => api.post('/telecalling/leads/assign-by-filters', data),

  getCounts: (params) => api.get('/telecalling/leads/counts', { params }),

  getStatusCounts: (params) => api.get('/telecalling/leads/status-counts', { params }),

  getByCategory: (category, params) =>
    api.get('/telecalling/leads', { params: { category, ...params } }),

  // Import leads with optional equal distribution
  importLeads: (data) => api.post('/telecalling/leads/import', data),

  exportCSV: (params) =>
    api.get('/telecalling/leads/export', { params, responseType: 'blob' }),

  // Get all unique sources
  getSources: () => api.get('/telecalling/leads/sources'),
};

// ========================================
// CALL LOGS API
// ========================================

export const callLogsAPI = {
  getAll: (params) => api.get('/telecalling/call-logs', { params }),

  getById: (id) => api.get(`/telecalling/call-logs/${id}`),

  create: (data) => api.post('/telecalling/call-logs', data),

  getStatistics: (params) => api.get('/telecalling/call-logs/statistics', { params }),

  getUsersWithPhones: () => api.get('/telecalling/call-logs/users-with-phones'),

  getByUser: (userId, params) =>
    api.get('/telecalling/call-logs', { params: { assignedTo: userId, ...params } }),
};

// ========================================
// RECORDINGS API (Server Storage)
// RECORDINGS API (Cloudinary)
// ========================================

export const recordingsAPI = {
  // Upload recording file
  upload: (formData) => api.post('/recordings/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  // Get all recordings (Admin)
  getAll: (params) => api.get('/recordings', { params }),

  // Get single recording
  getById: (id) => api.get(`/recordings/${id}`),

  // Get recording file URL
  getFileUrl: (id) => `${BASE_URL}/recordings/file/${id}`,

  // Delete recording
  delete: (id) => api.delete(`/recordings/${id}`),
};

// ========================================
// TASKS API
// ========================================

export const tasksAPI = {
  getAll: (params) => api.get('/telecalling/tasks', { params }),

  getById: (id) => api.get(`/telecalling/tasks/${id}`),

  create: (data) => api.post('/telecalling/tasks', data),

  update: (id, data) => api.put(`/telecalling/tasks/${id}`, data),

  complete: (id, notes) => api.put(`/telecalling/tasks/${id}/complete`, { notes }),

  delete: (id) => api.delete(`/telecalling/tasks/${id}`),
};

// ========================================
// DASHBOARD API
// ========================================

export const dashboardAPI = {
  getStats: (params) => api.get('/telecalling/dashboard', { params }),

  getDailyReport: (date) => api.get('/telecalling/reports/daily', { params: { date } }),

  getWeeklyReport: (startDate, endDate) =>
    api.get('/telecalling/reports/weekly', { params: { startDate, endDate } }),

  getMonthlyReport: (month, year) =>
    api.get('/telecalling/reports/monthly', { params: { month, year } }),

  getLeaderboard: (params) => api.get('/telecalling/reports/leaderboard', { params }),

  // CEO Dashboard alerts
  getAlerts: () => api.get('/telecalling/dashboard/alerts'),

  // Pending actions overview
  getPendingActions: (params) => api.get('/telecalling/dashboard/pending-actions', { params }),
};

// ========================================
// REPORTS API
// ========================================

export const reportsAPI = {
  getDailyCalls: (params) => api.get('/telecalling/reports/daily-calls', { params }),

  getWeeklyTrends: () => api.get('/telecalling/reports/weekly-trends'),

  getLeadSources: (params) => api.get('/telecalling/reports/lead-sources', { params }),

  // Product-wise performance
  getProductPerformance: (params) => api.get('/telecalling/reports/product-performance', { params }),

  // Follow-up statistics
  getFollowUpStats: (params) => api.get('/telecalling/reports/follow-up-stats', { params }),

  // Call status distribution
  getCallStatusDistribution: (params) => api.get('/telecalling/reports/call-status-distribution', { params }),

  // Follow-up status distribution
  getFollowUpStatusDistribution: (params) => api.get('/telecalling/reports/follow-up-status-distribution', { params }),

  // Lost reason analysis
  getLostReasonAnalysis: (params) => api.get('/telecalling/reports/lost-reason-analysis', { params }),

  // Lead & call details table
  getLeadCallDetails: (params) => api.get('/telecalling/reports/lead-call-details', { params }),

  // Team performance
  getTeamPerformance: (params) => api.get('/telecalling/reports/team-performance', { params }),

  // Team summary
  getTeamSummary: (params) => api.get('/telecalling/reports/team-summary', { params }),

  // Daily Work Reports
  getDailyWorkReport: (params) => api.get('/telecalling/reports/daily-work', { params }),

  getUserDailyReport: (userId, params) => api.get(`/telecalling/reports/daily-work/${userId}`, { params }),

  exportDailyWork: (params) => api.get('/telecalling/reports/daily-work-export', { params }),
};

// ========================================
// ATTENDANCE API
// ========================================

export const attendanceAPI = {
  // Get all attendance records
  getAll: (params) => api.get('/telecalling/attendance/all', { params }),

  // Get by user
  getByUser: (userId, params) =>
    api.get('/telecalling/attendance/all', { params: { userId, ...params } }),

  // Get statistics
  getStats: (params) => api.get('/telecalling/attendance/stats', { params }),

  // Get user-wise report
  getReport: (params) => api.get('/telecalling/attendance/report', { params }),

  // Export report
  exportReport: (params) =>
    api.get('/telecalling/attendance/export', { params }),

  // Check in
  checkIn: (data) => api.post('/telecalling/attendance/checkin', data),

  // Check out
  checkOut: (data) => api.post('/telecalling/attendance/checkout', data),

  // Get today's attendance
  getToday: () => api.get('/telecalling/attendance/today'),

  // Get attendance history
  getHistory: (params) => api.get('/telecalling/attendance/history', { params }),
};

// ========================================
// ALLOCATION API
// ========================================

export const allocationAPI = {
  // Get my allocation for today (telecaller)
  getMy: () => api.get('/telecalling/allocations/my'),

  // Get allocation report (admin/manager)
  getReport: (params) => api.get('/telecalling/allocations/report', { params }),

  // Get available unassigned leads count
  getAvailable: () => api.get('/telecalling/allocations/available'),

  // Manual allocate to user (admin/manager)
  allocate: (userId, limit) => api.post('/telecalling/allocations/allocate', { userId, limit }),

  // Batch allocate to all telecallers (admin only)
  batchAllocate: (limit) => api.post('/telecalling/allocations/batch', { limit }),

  // Reallocate pending leads (admin only)
  reallocate: (days, userId) => api.post('/telecalling/allocations/reallocate', { days, userId }),
};

// ========================================
// UPLOAD API
// ========================================

export const uploadAPI = {
  uploadFile: (formData) =>
    api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// ========================================
// WHATSAPP TEMPLATES API
// ========================================

export const whatsappTemplatesAPI = {
  // Get all templates (Admin)
  getAll: (params) => api.get('/whatsapp-templates', { params }),

  // Get active templates (For Mobile/Telecaller)
  getActive: () => api.get('/whatsapp-templates/active'),

  // Get single template
  getById: (id) => api.get(`/whatsapp-templates/${id}`),

  // Create template
  create: (data) => api.post('/whatsapp-templates', data),

  // Update template
  update: (id, data) => api.put(`/whatsapp-templates/${id}`, data),

  // Delete template
  delete: (id) => api.delete(`/whatsapp-templates/${id}`),

  // Toggle template status
  toggle: (id) => api.patch(`/whatsapp-templates/${id}/toggle`),
};

// ========================================
// PUBLIC API (No Auth Required)
// ========================================

export const publicAPI = {
  // Get all active plans
  getPlans: () => api.get('/public/plans'),

  // Get plan by slug
  getPlanBySlug: (slug) => api.get(`/public/plans/${slug}`),

  // Check slug availability
  checkSlug: (slug) => api.get(`/public/check-slug/${slug}`),

  // Check email availability
  checkEmail: (email) => api.get(`/public/check-email/${email}`),

  // Register organization
  register: (data) => api.post('/public/register', data),

  // Health check
  healthCheck: () => api.get('/public/health'),
};

// ========================================
// SUPER ADMIN API
// ========================================

export const superAdminAPI = {
  // Organizations
  getOrganizations: (params) => api.get('/superadmin/organizations', { params }),
  getOrganization: (id) => api.get(`/superadmin/organizations/${id}`),
  createOrganization: (data) => api.post('/superadmin/organizations', data),
  updateOrganization: (id, data) => api.patch(`/superadmin/organizations/${id}`, data),
  suspendOrganization: (id) => api.patch(`/superadmin/organizations/${id}/suspend`),
  activateOrganization: (id) => api.patch(`/superadmin/organizations/${id}/activate`),
  deleteOrganization: (id) => api.delete(`/superadmin/organizations/${id}`),
  createOrgAdmin: (orgId, data) => api.post(`/superadmin/organizations/${orgId}/create-admin`, data),

  // Plans
  getPlans: () => api.get('/superadmin/plans'),
  createPlan: (data) => api.post('/superadmin/plans', data),
  updatePlan: (id, data) => api.patch(`/superadmin/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/superadmin/plans/${id}`),

  // Leads (Demo Requests)
  getLeads: (params) => api.get('/leads/admin', { params }),
  getLead: (id) => api.get(`/leads/admin/${id}`),
  updateLeadStatus: (id, status) => api.patch(`/leads/admin/${id}/status`, { status }),

  // Analytics
  getOverview: () => api.get('/superadmin/analytics/overview'),
  getRevenueStats: () => api.get('/superadmin/analytics/revenue'),

  // Admins
  getAdmins: () => api.get('/superadmin/admins'),
  createAdmin: (data) => api.post('/superadmin/admins', data),
  updateAdmin: (id, data) => api.patch(`/superadmin/admins/${id}`, data),
  deleteAdmin: (id) => api.delete(`/superadmin/admins/${id}`),

  // Auth
  login: (email, password) => api.post('/superadmin/auth/login', { email, password }),
  getMe: () => api.get('/superadmin/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/superadmin/auth/change-password', { currentPassword, newPassword }),
};

// ========================================
// GOOGLE SHEET INTEGRATION API
// ========================================

export const googleSheetAPI = {
  // Get all API keys for the organization
  getApiKeys: () => api.get('/google-sheet/api-keys'),

  // Generate new API key
  createApiKey: (data) => api.post('/google-sheet/api-keys', data),

  // Update API key
  updateApiKey: (id, data) => api.put(`/google-sheet/api-keys/${id}`, data),

  // Delete API key
  deleteApiKey: (id) => api.delete(`/google-sheet/api-keys/${id}`),

  // Get Apps Script code
  getScript: (apiKeyId) => api.get('/google-sheet/script', { params: { apiKeyId } }),

  // Test API key connection (public endpoint)
  testConnection: (apiKey) => api.get('/google-sheet/test', {
    headers: { 'X-API-Key': apiKey }
  }),
};

// ========================================
// PIPELINE STAGES API
// ========================================

export const pipelineAPI = {
  // Get all pipeline stages for the organization
  getAll: () => api.get('/pipeline-stages'),

  // Get single pipeline stage
  getById: (id) => api.get(`/pipeline-stages/${id}`),

  // Create new pipeline stage
  create: (data) => api.post('/pipeline-stages', data),

  // Update pipeline stage
  update: (id, data) => api.put(`/pipeline-stages/${id}`, data),

  // Delete pipeline stage
  delete: (id) => api.delete(`/pipeline-stages/${id}`),

  // Reorder pipeline stages
  reorder: (stages) => api.put('/pipeline-stages/reorder/bulk', { stages }),

  // Reset to default stages
  resetDefaults: () => api.post('/pipeline-stages/reset-defaults'),

  // Get stage statistics (leads count per stage)
  getStats: (params) => api.get('/pipeline-stages/stats/summary', { params }),
};

// ========================================
// PRODUCTS API
// ========================================

export const productsAPI = {
  // Get all products for the organization (optional filters: active, search)
  getAll: (params) => api.get('/products', { params }),

  // Get single product
  getById: (id) => api.get(`/products/${id}`),

  // Create new product
  create: (data) => api.post('/products', data),

  // Update product
  update: (id, data) => api.put(`/products/${id}`, data),

  // Delete product
  delete: (id) => api.delete(`/products/${id}`),
};

// ========================================
// SCRIPTS API (telecaller call scripts)
// ========================================

export const scriptsAPI = {
  // Get all scripts (optional filters: productId, active, search)
  getAll: (params) => api.get('/scripts', { params }),

  // Get single script
  getById: (id) => api.get(`/scripts/${id}`),

  // Create new script
  create: (data) => api.post('/scripts', data),

  // Update script
  update: (id, data) => api.put(`/scripts/${id}`, data),

  // Delete script
  delete: (id) => api.delete(`/scripts/${id}`),
};

// ========================================
// INTEGRATIONS API
// ========================================

export const integrationsAPI = {
  // Get all integrations
  getAll: (params) => api.get('/webhook-integrations', { params }),

  // Get single integration
  getById: (id) => api.get(`/webhook-integrations/${id}`),

  // Create integration
  create: (data) => api.post('/webhook-integrations', data),

  // Update integration
  update: (id, data) => api.put(`/webhook-integrations/${id}`, data),

  // Delete integration
  delete: (id) => api.delete(`/webhook-integrations/${id}`),

  // Enable integration
  enable: (id) => api.post(`/webhook-integrations/${id}/enable`),

  // Disable integration
  disable: (id) => api.post(`/webhook-integrations/${id}/disable`),

  // Test integration connection
  test: (id) => api.post(`/webhook-integrations/${id}/test`),

  // Regenerate webhook secret
  regenerateSecret: (id) => api.post(`/webhook-integrations/${id}/regenerate-secret`),

  // Get integration logs
  getLogs: (id, params) => api.get(`/webhook-integrations/${id}/logs`, { params }),

  // Get log details
  getLogDetails: (integrationId, logId) => api.get(`/webhook-integrations/${integrationId}/logs/${logId}`),

  // Get integration statistics
  getStats: (id, params) => api.get(`/webhook-integrations/${id}/stats`, { params }),

  // Retry failed log
  retryLog: (integrationId, logId) => api.post(`/webhook-integrations/${integrationId}/logs/${logId}/retry`),

  // Get dashboard statistics
  getDashboardStats: (params) => api.get('/webhook-integrations/dashboard/stats', { params }),
};

export default api;